import {memo, useCallback, useMemo} from 'react'

import type {FieldOperation} from '../api/view/contracts'
import {AggregationSettingsContext, type FieldAggregate} from '../hooks/use-aggregation-settings'
import {ViewStateActionTypes} from '../hooks/use-view-state-reducer/view-state-action-types'
import {useViews} from '../hooks/use-views'
import type {ColumnModel} from '../models/column-model'
import type {GroupId} from '../state-providers/memex-items/queries/query-keys'
import {useFieldMetricsQuery} from '../state-providers/memex-items/queries/use-field-metrics-query'

export const AggregationSettingsProvider = memo<{
  children?: React.ReactNode
}>(function AggregationSettingsProvider({children}) {
  const {currentView, viewStateDispatch} = useViews()

  const addFieldAggregation = useCallback(
    (viewNumber: number, fieldOperation: FieldOperation, column: ColumnModel): void => {
      viewStateDispatch({type: ViewStateActionTypes.AddFieldAggregation, viewNumber, fieldOperation, column})
    },
    [viewStateDispatch],
  )

  const removeFieldAggregation = useCallback(
    (viewNumber: number, fieldOperation: FieldOperation, column: ColumnModel): void => {
      viewStateDispatch({type: ViewStateActionTypes.RemoveFieldAggregation, viewNumber, fieldOperation, column})
    },
    [viewStateDispatch],
  )

  const toggleItemsCount = useCallback(
    (viewNumber: number): void => {
      viewStateDispatch({type: ViewStateActionTypes.ToggleItemsCount, viewNumber})
    },
    [viewStateDispatch],
  )

  const fieldMetricsQuery = useFieldMetricsQuery()
  const fieldMetricsData = fieldMetricsQuery?.data

  const getAggregatesForGroupId = useCallback(
    (groupId: GroupId) => {
      const fieldAggregates: Array<FieldAggregate> = []
      const fieldMetricsForGroupId = fieldMetricsData?.groups[groupId]

      const fieldsToSum = currentView?.localViewStateDeserialized.aggregationSettings.sum

      if (fieldsToSum) {
        for (const field of fieldsToSum) {
          const fieldMetric = fieldMetricsForGroupId?.find(f => f.fieldId === field.id)
          if (fieldMetric) {
            const aggregate = {
              name: field.name,
              sum: fieldMetric.value,
            }
            fieldAggregates.push(aggregate)
          }
        }
      }
      return fieldAggregates
    },
    [currentView?.localViewStateDeserialized.aggregationSettings.sum, fieldMetricsData?.groups],
  )

  return (
    <AggregationSettingsContext.Provider
      value={useMemo(() => {
        return {
          hideItemsCount: !!currentView?.localViewStateDeserialized.aggregationSettings.hideItemsCount,
          sum: currentView?.localViewStateDeserialized.aggregationSettings.sum ?? [],
          toggleItemsCount,
          addFieldAggregation,
          removeFieldAggregation,
          isAggregationSettingsDirty: currentView?.isAggregationSettingsDirty ?? false,
          getAggregatesForGroupId,
        }
      }, [
        currentView?.localViewStateDeserialized.aggregationSettings.hideItemsCount,
        currentView?.localViewStateDeserialized.aggregationSettings.sum,
        currentView?.isAggregationSettingsDirty,
        toggleItemsCount,
        addFieldAggregation,
        removeFieldAggregation,
        getAggregatesForGroupId,
      ])}
    >
      {children}
    </AggregationSettingsContext.Provider>
  )
})

try{ AggregationSettingsProvider.displayName ||= 'AggregationSettingsProvider' } catch {}